import type { FC } from "react";

import KpiCard from "../../CommonComponents/Dashboard/KpiCard/KpiCard";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { KPI_CARD_BACKGROUND } from "../../../utils/Constants";

import { resolveKpiTile, type AffiliateKpisScope } from "../affiliatesData";
import useCurrencySymbol from "../../../hooks/useCurrencySymbol";
import type {
  AffiliateKpiCardKey,
  AffiliateKpiValue,
} from "../../../types/affiliates";
import type { AffiliateAnalyticsDataSource } from "../../../queries/affiliates/analyticsDataSource";
import type { KpiEligibleId } from "../NewAffiliate/AffiliatePamAccount/affiliatePamAccountData";

import DailyUsersAndFtds from "./DailyUsersAndFtds/DailyUsersAndFtds";
import TopFiveAffiliates from "./TopFiveAffiliates/TopFiveAffiliates";
import TopFiveCodes from "./TopFiveCodes/TopFiveCodes";
import AffiliatesByState from "./AffiliatesByState/AffiliatesByState";

import styles from "./AffiliateKpisOverview.module.css";

// `avgLtv` is FE-derived (Revenue / FTDs) and not in the BE catalogue.
export type AffiliateKpisMetricId = KpiEligibleId | "avgLtv";

// FE metric id → BE UPPER_SNAKE key. Used to translate the toggle
// selection into the chart-dropdown filter passed to sibling charts.
const METRIC_ID_TO_CHART_KEY: Record<AffiliateKpisMetricId, string> = {
  totalUsers: "TOTAL_USERS",
  totalFtds: "TOTAL_FTDS",
  ftdRate: "FTD_RATE",
  totalOwed: "TOTAL_OWED",
  totalRevenue: "TOTAL_REVENUE",
  avgLtv: "AVERAGE_LTV",
};

// `KpiCard` is still plain JS — cast until it gets TS types. Mirrors the
// pattern used elsewhere in Affiliates.
type KpiCardProps = {
  title: string;
  value: number;
  hugeValue?: boolean;
  percentage?: number;
  rate?: boolean;
  backgroundColor?: string;
  isApiRunning?: boolean;
  maximumFractionDigits?: number;
  currencySymbol?: string;
};
const TypedKpiCard = KpiCard as unknown as FC<KpiCardProps>;

export type AffiliateKpisInput =
  | Partial<Record<AffiliateKpiCardKey, AffiliateKpiValue>>
  | undefined;

export type AffiliateKpisOverviewProps = {
  // Raw `portfolio_kpis.kpis` (LIST page) or per-affiliate `kpis` block
  // (PROFILE page) — this component knows which 5 keys to pull and how
  // to render each tile, so callers stay ignorant of the KPI catalogue.
  kpis: AffiliateKpisInput;
  isLoading: boolean;
  scope?: AffiliateKpisScope;
  // Set on the profile so the charts hit the per-affiliate endpoints.
  affiliateId?: string;
  // Omit to show every card (operator default); pass a subset to gate.
  visibleMetrics?: ReadonlyArray<AffiliateKpisMetricId>;
  // Hide the chart block entirely (e.g. compact embeds).
  showCharts?: boolean;
  // "portal" routes the chart fetches through the affiliate-portal
  // endpoints + token; default is the operator tree.
  dataSource?: AffiliateAnalyticsDataSource;
  // Optional cross-cut filters forwarded to every chart hook. Set on the
  // list KPIs tab when the operator picks a specific affiliate / code;
  // undefined = no filter (all rows).
  affiliate?: string;
  promoCode?: string;
  // Options for the charts' own All Affiliates selects (Daily / Geo) and
  // the Top 5 bar labels (id → name).
  affiliateOptions?: ReadonlyArray<{ value: string; label: string }>;
  isLoadingAffiliates?: boolean;
  // Options for the charts' own All Codes selects (Daily / Geo).
  codeOptions?: ReadonlyArray<{ value: string; label: string }>;
  isLoadingCodes?: boolean;
};

// Average LTV is a derived KPI — Total Revenue divided by Total FTDs.
// The BE does NOT ship `AVERAGE_LTV` on `portfolio_kpis.kpis`; computing
// it here keeps the FE the single source of truth for the formula.
//
//   - Value: revenue / ftds, with a guard so an empty period (no FTDs)
//     renders 0 instead of NaN / Infinity.
//   - Percentage: same ratio applied to the two source change_pct values
//     so the trend indicator on the tile actually tracks LTV (not one of
//     the inputs). Falls back to undefined when either source % is
//     missing (null change_pct from the BE = "no prior period") or when
//     a denominator would collapse to <= 0.
const deriveAverageLtv = (
  revenue: { value: number; percentage: number | undefined },
  ftds: { value: number; percentage: number | undefined },
): { value: number; percentage: number | undefined } => {
  const value = ftds.value > 0 ? revenue.value / ftds.value : 0;

  if (revenue.percentage === undefined || ftds.percentage === undefined) {
    return { value, percentage: undefined };
  }
  const revenueFactor = 1 + revenue.percentage / 100;
  const ftdsFactor = 1 + ftds.percentage / 100;
  if (ftdsFactor <= 0 || revenueFactor < 0) {
    return { value, percentage: undefined };
  }
  return { value, percentage: (revenueFactor / ftdsFactor - 1) * 100 };
};

/**
 * Presentational overview block — the 6 KPI tiles followed by the four
 * chart sub-components. Used as-is on the AffiliateProfile page (no
 * filter row above it) and wrapped by `AffiliateKpis` on the LIST page
 * (which adds the affiliate / code / date / download filter row).
 */
const AffiliateKpisOverview: FC<AffiliateKpisOverviewProps> = ({
  kpis,
  isLoading,
  scope = "module",
  affiliateId,
  visibleMetrics,
  showCharts = true,
  dataSource = "operator",
  affiliate,
  promoCode,
  affiliateOptions,
  isLoadingAffiliates,
  codeOptions,
  isLoadingCodes,
}) => {
  const isAffiliateScope = scope === "affiliate";
  const currencySymbol = useCurrencySymbol() ?? "$";
  const totalUsersKpi = resolveKpiTile(kpis, "TOTAL_USERS");
  const totalFtdsKpi = resolveKpiTile(kpis, "TOTAL_FTDS");
  const ftdRateKpi = resolveKpiTile(kpis, "FTD_RATE");
  const totalOwedKpi = resolveKpiTile(kpis, "TOTAL_OWED");
  const totalRevenueKpi = resolveKpiTile(kpis, "TOTAL_REVENUE");
  // BE ships AVERAGE_LTV since PAMENG-2222; fall back to the FE-derived
  // Revenue / FTDs so pre-2222 responses (or the portal-restricted
  // response that strips it) still render a tile.
  const beAverageLtv = kpis?.AVERAGE_LTV;
  const averageLtvKpi = beAverageLtv
    ? {
        value: beAverageLtv.value,
        percentage: beAverageLtv.change_pct ?? undefined,
      }
    : deriveAverageLtv(totalRevenueKpi, totalFtdsKpi);

  const showMetric = (id: AffiliateKpisMetricId): boolean =>
    !visibleMetrics || visibleMetrics.includes(id);

  // Undefined → children keep their full option catalogue (operator default).
  // Explicit set → each chart intersects its own MetricKey union with these.
  const chartVisibleMetrics = visibleMetrics
    ? visibleMetrics.map((id) => METRIC_ID_TO_CHART_KEY[id])
    : undefined;

  // Affiliate scope without an id = previewing an affiliate that doesn't
  // exist yet; the hooks would fall back to module-wide routes and show
  // operator-wide numbers. Portal requests are token-scoped, no id needed.
  const chartQueriesEnabled =
    dataSource === "portal" || !isAffiliateScope || Boolean(affiliateId);

  return (
    <>
      <div className={styles.kpisRow}>
        {showMetric("totalUsers") && (
          <div className={styles.kpiCardWrapper}>
            <TypedKpiCard
              title={AppStrings.TOTAL_USERS}
              value={totalUsersKpi.value}
              percentage={totalUsersKpi.percentage}
              isApiRunning={isLoading}
              backgroundColor={KPI_CARD_BACKGROUND.DARK}
              maximumFractionDigits={0}
            />
          </div>
        )}
        {showMetric("totalFtds") && (
          <div className={styles.kpiCardWrapper}>
            <TypedKpiCard
              title={AppStrings.TOTAL_FTDS}
              value={totalFtdsKpi.value}
              percentage={totalFtdsKpi.percentage}
              isApiRunning={isLoading}
              backgroundColor={KPI_CARD_BACKGROUND.DARK}
              maximumFractionDigits={0}
            />
          </div>
        )}
        {showMetric("ftdRate") && (
          <div className={styles.kpiCardWrapper}>
            <TypedKpiCard
              title={AppStrings.FTD_RATE}
              value={ftdRateKpi.value}
              percentage={ftdRateKpi.percentage}
              isApiRunning={isLoading}
              rate
              backgroundColor={KPI_CARD_BACKGROUND.DARK}
            />
          </div>
        )}
        {showMetric("totalOwed") && (
          <div className={styles.kpiCardWrapper}>
            <TypedKpiCard
              title={AppStrings.TOTAL_OWED}
              value={totalOwedKpi.value}
              percentage={totalOwedKpi.percentage}
              isApiRunning={isLoading}
              backgroundColor={KPI_CARD_BACKGROUND.DARK}
              currencySymbol={currencySymbol}
            />
          </div>
        )}
        {showMetric("totalRevenue") && (
          <div className={styles.kpiCardWrapper}>
            <TypedKpiCard
              title={AppStrings.TOTAL_REVENUE}
              value={totalRevenueKpi.value}
              percentage={totalRevenueKpi.percentage}
              isApiRunning={isLoading}
              backgroundColor={KPI_CARD_BACKGROUND.DARK}
              currencySymbol={currencySymbol}
            />
          </div>
        )}
        {showMetric("avgLtv") && (
          <div className={styles.kpiCardWrapper}>
            <TypedKpiCard
              title={AppStrings.AVERAGE_LTV}
              value={averageLtvKpi.value}
              percentage={averageLtvKpi.percentage}
              isApiRunning={isLoading}
              backgroundColor={KPI_CARD_BACKGROUND.DARK}
              currencySymbol={currencySymbol}
            />
          </div>
        )}
      </div>

      {showCharts && (
        <>
          {/* The daily chart hardcodes the users + FTDs series (no metric
              dropdown to filter), so it only renders while at least one of
              its two source metrics is shared. */}
          {(showMetric("totalUsers") || showMetric("totalFtds")) && (
            <div className={styles.chartRow}>
              <DailyUsersAndFtds
                scope={scope}
                affiliateId={affiliateId}
                dataSource={dataSource}
                affiliate={affiliate}
                promoCode={promoCode}
                affiliateOptions={affiliateOptions}
                isLoadingAffiliates={isLoadingAffiliates}
                codeOptions={codeOptions}
                isLoadingCodes={isLoadingCodes}
                queryEnabled={chartQueriesEnabled}
              />
            </div>
          )}

          {isAffiliateScope ? (
            // Top 5 Affiliates doesn't apply inside one affiliate.
            <div className={styles.chartRow} data-testid="affiliate-charts-row">
              <TopFiveCodes
                affiliateId={affiliateId}
                visibleMetrics={chartVisibleMetrics}
                dataSource={dataSource}
                promoCode={promoCode}
                queryEnabled={chartQueriesEnabled}
              />
            </div>
          ) : (
            <div className={styles.chartsRow} data-testid="affiliate-charts-row">
              <TopFiveAffiliates
                visibleMetrics={chartVisibleMetrics}
                affiliate={affiliate}
                promoCode={promoCode}
                affiliateOptions={affiliateOptions}
              />
              <TopFiveCodes
                affiliateId={affiliateId}
                visibleMetrics={chartVisibleMetrics}
                dataSource={dataSource}
                affiliate={affiliate}
                promoCode={promoCode}
              />
            </div>
          )}

          <div className={styles.chartRow}>
            <AffiliatesByState
              scope={scope}
              affiliateId={affiliateId}
              visibleMetrics={chartVisibleMetrics}
              dataSource={dataSource}
              affiliate={affiliate}
              promoCode={promoCode}
              affiliateOptions={affiliateOptions}
              isLoadingAffiliates={isLoadingAffiliates}
              codeOptions={codeOptions}
              isLoadingCodes={isLoadingCodes}
              queryEnabled={chartQueriesEnabled}
            />
          </div>
        </>
      )}
    </>
  );
};

export default AffiliateKpisOverview;
